import React, { type JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

type LocalizedTextProps<
  T extends
    | keyof JSX.IntrinsicElements
    | React.ComponentType<JSX.IntrinsicElements | React.Component>,
> = {
  as?: T;
  children: React.ReactNode;
  fontSize?: string;
  mmFontSize?: string;
  style?: React.CSSProperties;
  className?: string; // Add className prop type
} & React.ComponentProps<T>;

/**
 * LocalizedText is a flexible React component for rendering text with automatic font and size adjustments
 * based on the current language (supports Myanmar and default).
 *
 * @template T - The HTML element or React component to render as (default: 'span').
 *
 * @param {Object} props - Component props.
 * @param {T} [props.as='span'] - The element or component to render as.
 * @param {React.ReactNode} props.children - The content to display inside the component.
 * @param {string} [props.fontSize='24px'] - Font size for non-Myanmar languages.
 * @param {string} [props.mmFontSize='20px'] - Font size for Myanmar language.
 * @param {React.CSSProperties} [props.style] - Additional CSS styles to apply.
 * @param {...React.ComponentProps<T>} [rest] - Additional props passed to the rendered component.
 *
 * @returns {JSX.Element} The rendered element with localized font and size.
 *
 * @example
 * <LocalizedText fontSize="18px" mmFontSize="16px">
 *   {t('hello')}
 * </LocalizedText>
 *
 * @example
 * <LocalizedText as="h1" style={{ color: 'red' }}>
 *   {t('welcome')}
 * </LocalizedText>
 */
function LocalizedText<
  T extends keyof JSX.IntrinsicElements | React.JSXElementConstructor<unknown> = 'span',
>({
  as = 'span' as T,
  children,
  fontSize = '24px',
  mmFontSize = '20px',
  style = {},
  className = '',
  ...rest
}: LocalizedTextProps<T>): JSX.Element {
  const { i18n } = useTranslation();
  const isMyanmar = i18n.language === 'mm';
  const Component = as;

  const defaultClasses = useMemo(() => {
    return isMyanmar ? 'font-myanmar' : 'font-sans';
  }, [isMyanmar]);

  const mergedStyle = useMemo(
    () => ({
      fontSize: isMyanmar ? mmFontSize : fontSize,
      lineHeight: isMyanmar ? '1.8' : '1.6',
      ...style,
    }),
    [isMyanmar, fontSize, mmFontSize, style],
  );

  return React.createElement(
    Component,
    {
      style: mergedStyle,
      className: cn(defaultClasses, className), // Merge default classes with provided className
      ...rest,
    },
    children,
  );
}

export default LocalizedText;
